/**
 * CSV Parser utility for Satellite Talent Dashboard
 * Parses CSV files from local sources
 */

/**
 * Parse CSV string into array of objects
 * @param {string} csvString - Raw CSV string content
 * @returns {Array<Object>} - Array of objects representing rows
 */
export const parseCSV = (csvString) => {
  // Split by rows, but handle multiline entries within quotes
  const rows = parseCSVRows(csvString);
  
  // Extract headers from first row and handle duplicates by adding suffixes
  const originalHeaders = splitCSVRow(rows[0]).map(header => header.trim());
  const headers = [];
  const headerCounts = {};
  
  originalHeaders.forEach(header => {
    if (headerCounts[header]) {
      headerCounts[header]++;
      headers.push(`${header}.${headerCounts[header] - 1}`);
    } else {
      headerCounts[header] = 1;
      headers.push(header);
    }
  });
  
  // Headers processed with duplicate handling
  
  // Parse data rows (skip header row)
  return rows.slice(1).filter(row => row.trim()).map(row => {
    // Split by commas, but preserve commas within quotes
    const values = splitCSVRow(row);
    
    // Create object from headers and values
    const rowData = {};
    headers.forEach((header, index) => {
      rowData[header] = values[index] ? values[index].trim() : '';
    });
    
    return rowData;
  });
};

/**
 * Parse CSV string into rows, handling multiline entries within quotes
 * This function properly handles cases where CSV fields contain newlines within quotes,
 * ensuring that such entries are treated as single records rather than being split
 * into multiple records. This fixes issues where candidates like Hassan Murtaza 
 * (with multiline comments) were being counted incorrectly.
 * @param {string} csvString - Raw CSV string content
 * @returns {Array<string>} - Array of complete CSV rows
 */
const parseCSVRows = (csvString) => {
  const rows = [];
  let currentRow = '';
  let insideQuotes = false;
  let i = 0;
  
  while (i < csvString.length) {
    const char = csvString[i];
    const nextChar = csvString[i + 1];
    
    if (char === '"' && currentRow === '') {
      // Start of quoted value at beginning of row
      insideQuotes = true;
      currentRow += char;
    } else if (char === '"' && nextChar === '"') {
      // Escaped quote within quoted value
      currentRow += '""';
      i++; // Skip next quote
    } else if (char === '"') {
      // Toggle quote state
      insideQuotes = !insideQuotes;
      currentRow += char;
    } else if ((char === '\n' || char === '\r') && !insideQuotes) {
      // End of row (only if not inside quotes)
      if (currentRow.trim()) {
        rows.push(currentRow.trim());
      }
      currentRow = '';
      // Skip \r\n combination
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      // Regular character or newline inside quotes
      currentRow += char;
    }
    
    i++;
  }
  
  // Push last row if exists
  if (currentRow.trim()) {
    rows.push(currentRow.trim());
  }
  
  return rows;
};

/**
 * Split CSV row respecting quoted values (which may contain commas)
 * @param {string} row - CSV row string
 * @returns {Array<string>} - Array of cell values
 */
const splitCSVRow = (row) => {
  const values = [];
  let currentValue = '';
  let insideQuote = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    const nextChar = row[i + 1];
    
    if (char === '"' && currentValue === '') {
      // Start of quoted value
      insideQuote = true;
    } else if (char === '"' && nextChar === '"') {
      // Escaped quote within quoted value
      currentValue += '"';
      i++; // Skip next quote
    } else if (char === '"') {
      // End of quoted value
      insideQuote = false;
    } else if (char === ',' && !insideQuote) {
      // End of value
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Push last value
  values.push(currentValue);
  
  return values;
};

/**
 * Get the correct path to CSV files based on environment
 * @param {string} filename - CSV filename
 * @returns {string} The correct path
 */
const getCSVPath = (filename) => {
  // Files are now in the public/docs folder for both development and production
  return `/docs/${filename}`;
};

/**
 * Read and parse the offers CSV file
 * @returns {Array<Object>} Parsed offer data
 */
export const parseOfferData = async () => {
  try {
    const filePath = getCSVPath('Satellite Offer Form - final (Responses) - Form Responses 1.csv');
    console.log(`Fetching offer data from: ${filePath}`);
    
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch offer data: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    return parseCSV(csvText);
  } catch (error) {
    console.error('Error parsing offer CSV:', error);
    throw error;
  }
};

/**
 * Read and parse the feedback survey CSV file
 * @returns {Array<Object>} Parsed feedback data
 */
export const parseFeedbackData = async () => {
  try {
    const filePath = getCSVPath('Satellite Talent - Quarterly Client Feedback Survey (Responses) - Form Responses 1.csv');
    console.log(`Fetching feedback data from: ${filePath}`);
    
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch feedback data: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    return parseFeedbackCSV(csvText);
  } catch (error) {
    console.error('Error parsing feedback CSV:', error);
    throw error;
  }
};

/**
 * Parse feedback CSV with custom logic for multiline headers
 * @param {string} csvString - Raw CSV string content
 * @returns {Array<Object>} - Array of objects representing feedback rows
 */
const parseFeedbackCSV = (csvString) => {
  // Split by rows
  const rows = csvString.split(/\r?\n/);
  
  // The feedback CSV has a specific structure - let's define the headers manually
  const headers = [
    'Timestamp',
    'Email Address',
    'Which Talent Partner are you currently working with ?',
    'How would you rate your overall experience working with the Talent Partner mentioned above? (0 = terrible, 10 = awesome)',
    'What have been the most valuable aspects of working with this Talent Partner?',
    'Do you have any suggestions on how we can improve our service?',
    'How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'
  ];
  
  // Find the first data row (skip header rows)
  let dataStartIndex = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].startsWith('3/') || rows[i].startsWith('4/') || rows[i].startsWith('5/') || rows[i].startsWith('6/') || rows[i].startsWith('7/')) {
      dataStartIndex = i;
      break;
    }
  }
  
  // Parse data rows
  return rows.slice(dataStartIndex)
    .filter(row => row.trim() && !row.startsWith('q,')) // Filter out empty rows and the "q" row
    .map(row => {
      // Split by commas, but preserve commas within quotes
      const values = splitCSVRow(row);
      
      // Create object from headers and values
      const rowData = {};
      headers.forEach((header, index) => {
        rowData[header] = values[index] ? values[index].trim() : '';
      });
      
      return rowData;
    });
};
